import { featureCollection, point } from '@turf/helpers';
import type { CircleLayerSpecification } from 'mapbox-gl';
import { useMemo } from 'react';
import { Layer, Source } from 'react-map-gl/mapbox';
import { BORDERS_WEIGHT_MD, COLOR_BACKGROUND_TINTED, COLOR_BORDER_INTERACTIVE } from '@alltrails/denali/tokens';
import type { MarkerProperties } from '@alltrails/types/CustomRoute';
import useMap from '../../../hooks/useMap';
import { useAppSelector } from '../../../redux';
import { selectCustomRouteActiveSegment } from '../../../redux/customRoute';
import { getAdjacentEndpointToLink } from '../../../utils/customRoutes/segmentLinking';
import { customRouteLinkableEndpointId, getLayerBeforeId } from '../../../utils/layers';
import useCanLinkSegments from '../useCanLinkSegments';
import { LINK_DISTANCE_PX } from './useAdjacentEndpointLinking';
import { getMarkerEnd } from './useDragMarker';
import { getExtendedEnd } from './useRouteToPoint';

const SOURCE_ID = 'custom-route-linkable-endpoint-source';

// The circle covers the area a point snaps and links within
const paint: CircleLayerSpecification['paint'] = {
  'circle-color': COLOR_BACKGROUND_TINTED,
  'circle-radius': LINK_DISTANCE_PX,
  'circle-stroke-color': COLOR_BORDER_INTERACTIVE,
  'circle-stroke-width': BORDERS_WEIGHT_MD
};

type LinkableEndpointIndicatorProps = {
  dragMarker?: MarkerProperties;
};

/**
 * Marks the neighboring segment's endpoint that the next placed point, or the dragged start or end handle, links to when placed near it.
 * Ordered with the feature layers but takes no events, so clicks on it still reach the base map and place points.
 */
const LinkableEndpointIndicator = ({ dragMarker }: LinkableEndpointIndicatorProps) => {
  const map = useMap();
  const canLinkSegments = useCanLinkSegments();
  const { activeSegment, activeSegmentIndex, isInCutMode, segments, selectedHandleIndex } = useAppSelector(state => ({
    activeSegment: selectCustomRouteActiveSegment(state),
    activeSegmentIndex: state.customRoute.activeSegmentIndex,
    isInCutMode: state.customRoute.cutData !== undefined,
    segments: state.customRoute.route.segments,
    selectedHandleIndex: state.customRoute.selectedHandleIndex
  }));

  const data = useMemo(() => {
    // A segment not added yet is one past the last
    const editingSegmentIndex = activeSegmentIndex ?? segments.length;
    const isDraggingEditingSegment = dragMarker && dragMarker.segmentIndex === editingSegmentIndex;
    const end = dragMarker ? isDraggingEditingSegment && getMarkerEnd(dragMarker) : getExtendedEnd(activeSegment, selectedHandleIndex);
    const endpoint = canLinkSegments && !isInCutMode && end ? getAdjacentEndpointToLink(segments, editingSegmentIndex, end) : undefined;
    return featureCollection(endpoint ? [point([endpoint[1], endpoint[0]])] : []);
  }, [activeSegment, activeSegmentIndex, canLinkSegments, dragMarker, isInCutMode, segments, selectedHandleIndex]);

  const beforeId = getLayerBeforeId(map, customRouteLinkableEndpointId);

  return (
    <Source id={SOURCE_ID} type="geojson" data={data}>
      <Layer id={customRouteLinkableEndpointId} source={SOURCE_ID} type="circle" paint={paint} beforeId={beforeId} />
    </Source>
  );
};

export default LinkableEndpointIndicator;
